// client/src/pages/admin/AdminCatalogDiscounts.js
// Admin management for catalog-wide discounts + brand registry
// Features: wholesaler filter dropdown, type filter, status filter, search, toggle, delete

import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmModal from '../../components/common/ConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../services/api';
import axios from 'axios';
import { Tag, ToggleLeft, ToggleRight, Trash2, Layers, Target, Search, Package, ChevronDown, ChevronUp } from 'lucide-react';

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AdminCatalogDiscounts = () => {
  const { t } = useTranslation('admin');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('discounts');
  const [discounts, setDiscounts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [brandStats, setBrandStats] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [wholesalers, setWholesalers] = useState([]);
  const [filters, setFilters] = useState({ active: '', type: '', search: '', wholesalerId: '' });
  const [expandedBrand, setExpandedBrand] = useState(null);
  const [brandProducts, setBrandProducts] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/admin/login');
  }, [user, navigate]);

  // Fetch wholesaler list for filter dropdown
  const fetchWholesalers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/platform/wholesalers-list`, { headers: getHeaders() });
      if (res.data.wholesalers && res.data.wholesalers.length > 0) {
        setWholesalers(res.data.wholesalers);
        return;
      }
    } catch (err) {
      console.error('Failed to fetch wholesalers-list:', err.response?.status, err.message);
    }
    // Fallback 1: try to extract unique wholesalers from catalog discounts
    try {
      const res = await axios.get(`${API_URL}/admin/platform/catalog-discounts?limit=500`, { headers: getHeaders() });
      const unique = [];
      const seen = new Set();
      (res.data.discounts || []).forEach(d => {
        if (d.wholesalerId?._id && !seen.has(d.wholesalerId._id)) {
          seen.add(d.wholesalerId._id);
          unique.push({ _id: d.wholesalerId._id, companyName: d.wholesalerId.companyName });
        }
      });
      if (unique.length > 0) { setWholesalers(unique); return; }
    } catch { /* silent */ }
    // Fallback 2: try admin users endpoint
    try {
      const res = await axios.get(`${API_URL}/admin/users/wholesalers?limit=100`, { headers: getHeaders() });
      const list = (res.data.wholesalers || res.data.users || []).map(w => ({
        _id: w.wholesalerProfile?._id || w._id,
        companyName: w.wholesalerProfile?.companyName || w.companyName || w.name || 'Unknown'
      })).filter(w => w._id);
      setWholesalers(list);
    } catch { /* silent */ }
  }, []);

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.active) params.append('active', filters.active);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      if (filters.wholesalerId) params.append('wholesalerId', filters.wholesalerId);
      const res = await axios.get(`${API_URL}/admin/platform/catalog-discounts?${params}`, { headers: getHeaders() });
      setDiscounts(res.data.discounts || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/platform/catalog-discounts/stats`, { headers: getHeaders() });
      setStats(res.data.stats);
    } catch (err) { console.error(err); }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const params = filters.search ? `?search=${filters.search}` : '';
      const [brandsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/platform/brands${params}`, { headers: getHeaders() }),
        axios.get(`${API_URL}/admin/platform/brands/stats`, { headers: getHeaders() })
      ]);
      setBrands(brandsRes.data.brands || []);
      setBrandStats(statsRes.data.stats);
    } catch (err) { console.error(err); }
  }, [filters.search]);

  useEffect(() => { fetchWholesalers(); }, [fetchWholesalers]);
  useEffect(() => {
    if (tab === 'discounts') { fetchDiscounts(); fetchStats(); }
    else { fetchBrands(); }
  }, [tab, fetchDiscounts, fetchStats, fetchBrands]);

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API_URL}/admin/platform/catalog-discounts/${id}/toggle`, {}, { headers: getHeaders() });
      fetchDiscounts(); fetchStats();
    } catch (err) { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (discountId) => {
    setConfirmModal({
      title: t('catalogDiscounts.deleteTitle', 'Delete Discount'),
      message: t('catalogDiscounts.confirmDelete'),
      icon: 'danger',
      confirmLabel: t('catalogDiscounts.deleteButton', 'Delete'),
      confirmColor: 'red',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await axios.delete(`${API_URL}/admin/platform/catalog-discounts/${discountId}`, { headers: getHeaders() });
          fetchDiscounts(); fetchStats();
        } catch (err) { toast.error('Failed to delete'); }
      }
    });
  };

  const typeColors = {
    flat: { bg: 'bg-blue-100', text: 'text-blue-700' },
    tiered: { bg: 'bg-purple-100', text: 'text-purple-700' },
    targeted: { bg: 'bg-emerald-100', text: 'text-emerald-700' }
  };
  const TypeIcon = ({ type }) => {
    if (type === 'flat') return <Tag className="w-4 h-4" />;
    if (type === 'tiered') return <Layers className="w-4 h-4" />;
    return <Target className="w-4 h-4" />;
  };
  const getDiscountValue = (d) => {
    if (d.type === 'flat') return `${d.percentage}%`;
    if (d.type === 'tiered') return `${d.tiers?.length || 0} tiers`;
    return `${d.targetPercentage}% on ${d.targetType}: ${d.targetValue}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Tag className="w-6 h-6 text-teal-500" /> {t('catalogDiscounts.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('catalogDiscounts.subtitle')}</p>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 mb-6">
            <button onClick={() => setTab('discounts')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'discounts' ? 'bg-teal-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>
              <Tag className="w-4 h-4 inline mr-1.5" />{t('catalogDiscounts.title')}
            </button>
            <button onClick={() => setTab('brands')}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'brands' ? 'bg-teal-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>
              <Package className="w-4 h-4 inline mr-1.5" />{t('brands.title')}
            </button>
          </div>

          {/* ═══ DISCOUNTS TAB ═══ */}
          {tab === 'discounts' && (
            <>
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                  {[
                    { label: t('catalogDiscounts.stats.total'), value: stats.total, color: 'text-gray-900' },
                    { label: t('catalogDiscounts.stats.active'), value: stats.active, color: 'text-green-600' },
                    { label: t('catalogDiscounts.stats.inactive'), value: stats.inactive, color: 'text-gray-500' },
                    { label: t('catalogDiscounts.stats.flat'), value: stats.flat, color: 'text-blue-600' },
                    { label: t('catalogDiscounts.stats.tiered'), value: stats.tiered, color: 'text-purple-600' },
                    { label: t('catalogDiscounts.stats.targeted'), value: stats.targeted, color: 'text-emerald-600' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-lg border p-3">
                      <p className="text-[11px] text-gray-400 uppercase">{s.label}</p>
                      <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Filters — with wholesaler dropdown */}
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input type="text" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                    placeholder={t('catalogDiscounts.filters.searchPlaceholder')}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" />
                </div>
                {/* Wholesaler filter */}
                <select value={filters.wholesalerId} onChange={e => setFilters(f => ({ ...f, wholesalerId: e.target.value }))}
                  className="px-3 py-2 border rounded-lg text-sm bg-white min-w-[180px]">
                  <option value="">{t('catalogDiscounts.filters.allWholesalers')}</option>
                  {wholesalers.map(w => (
                    <option key={w._id} value={w._id}>{w.companyName}</option>
                  ))}
                </select>
                <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                  className="px-3 py-2 border rounded-lg text-sm bg-white">
                  <option value="">{t('catalogDiscounts.filters.allTypes')}</option>
                  <option value="flat">{t('catalogDiscounts.types.flat')}</option>
                  <option value="tiered">{t('catalogDiscounts.types.tiered')}</option>
                  <option value="targeted">{t('catalogDiscounts.types.targeted')}</option>
                </select>
                {[
                  { v: '', l: t('catalogDiscounts.filters.allStatuses') },
                  { v: 'true', l: t('catalogDiscounts.filters.active') },
                  { v: 'false', l: t('catalogDiscounts.filters.inactive') }
                ].map(f => (
                  <button key={f.v} onClick={() => setFilters(prev => ({ ...prev, active: f.v }))}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${filters.active === f.v ? 'bg-teal-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-50'}`}>
                    {f.l}
                  </button>
                ))}
              </div>

              {/* Table */}
              <div className="bg-white rounded-lg border">
                {loading ? (
                  <div className="p-8 flex justify-center"><LoadingSpinner /></div>
                ) : discounts.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">{t('catalogDiscounts.noDiscounts')}</div>
                ) : (
                  <>
                    <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b text-[11px] text-gray-400 uppercase font-medium">
                      <div className="col-span-2">{t('catalogDiscounts.wholesaler')}</div>
                      <div className="col-span-1">{t('catalogDiscounts.type')}</div>
                      <div className="col-span-2">{t('catalogDiscounts.name')}</div>
                      <div className="col-span-2">{t('catalogDiscounts.value')}</div>
                      <div className="col-span-1 text-center">{t('catalogDiscounts.stackable')}</div>
                      <div className="col-span-1 text-center">{t('catalogDiscounts.status')}</div>
                      <div className="col-span-2">{t('catalogDiscounts.expires')}</div>
                      <div className="col-span-1 text-center">{t('catalogDiscounts.actions')}</div>
                    </div>
                    <div className="divide-y">
                      {discounts.map(d => {
                        const tc = typeColors[d.type] || typeColors.flat;
                        const isExpired = d.expiresAt && new Date(d.expiresAt) < new Date();
                        return (
                          <div key={d._id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50">
                            <div className="col-span-2 text-sm font-medium text-gray-900 truncate">
                              {d.wholesalerId?.companyName || '—'}
                              <span className="block text-[10px] text-gray-400 capitalize">{d.wholesalerId?.subscriptionTier}</span>
                            </div>
                            <div className="col-span-1">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${tc.bg} ${tc.text}`}>
                                <TypeIcon type={d.type} /> {t(`catalogDiscounts.types.${d.type}`)}
                              </span>
                            </div>
                            <div className="col-span-2 text-sm text-gray-800 truncate">{d.name}</div>
                            <div className="col-span-2 text-sm text-gray-600 truncate">{getDiscountValue(d)}</div>
                            <div className="col-span-1 text-center">
                              <span className={`text-xs font-medium ${d.stackable ? 'text-yellow-600' : 'text-gray-400'}`}>
                                {d.stackable ? t('catalogDiscounts.yes') : t('catalogDiscounts.no')}
                              </span>
                            </div>
                            <div className="col-span-1 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                d.active && !isExpired ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                                {d.active && !isExpired ? t('catalogDiscounts.filters.active') : isExpired ? 'Expired' : t('catalogDiscounts.filters.inactive')}
                              </span>
                            </div>
                            <div className="col-span-2 text-xs text-gray-500">
                              {d.expiresAt ? new Date(d.expiresAt).toLocaleDateString() : t('catalogDiscounts.never')}
                            </div>
                            <div className="col-span-1 flex justify-center gap-1">
                              <button onClick={() => handleToggle(d._id)} title="Toggle" className="p-1.5 rounded hover:bg-gray-100">
                                {d.active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                              </button>
                              <button onClick={() => handleDelete(d._id)} title="Delete" className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* ═══ BRANDS TAB ═══ */}
          {tab === 'brands' && (
            <>
              {brandStats && (
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-lg border p-4">
                    <p className="text-xs text-gray-400 uppercase">{t('brands.stats.total')}</p>
                    <p className="text-2xl font-bold text-gray-900">{brandStats.total}</p>
                  </div>
                  <div className="bg-white rounded-lg border p-4">
                    <p className="text-xs text-gray-400 uppercase">{t('brands.stats.active')}</p>
                    <p className="text-2xl font-bold text-green-600">{brandStats.active}</p>
                  </div>
                </div>
              )}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input type="text" value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  placeholder={t('brands.name') + '...'} className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm max-w-sm" />
              </div>
              <div className="bg-white rounded-lg border">
                {brands.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">{t('brands.noBrands')}</div>
                ) : (
                  <>
                    <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 border-b text-[11px] text-gray-400 uppercase font-medium">
                      <div className="col-span-5">{t('brands.name')}</div>
                      <div className="col-span-3 text-center">{t('brands.productCount')}</div>
                      <div className="col-span-4">{t('catalogDiscounts.created')}</div>
                    </div>
                    <div className="divide-y">
                      {brands.map(b => (
                        <div key={b._id}>
                          <div 
                            className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50 cursor-pointer"
                            onClick={async () => {
                              if (expandedBrand === b._id) {
                                setExpandedBrand(null);
                                return;
                              }
                              setExpandedBrand(b._id);
                              if (!brandProducts[b._id]) {
                                try {
                                  const res = await axios.get(`${API_URL}/admin/platform/brands/${encodeURIComponent(b.name)}/products`, { headers: getHeaders() });
                                  setBrandProducts(prev => ({ ...prev, [b._id]: res.data.products || [] }));
                                } catch { setBrandProducts(prev => ({ ...prev, [b._id]: [] })); }
                              }
                            }}
                          >
                            <div className="col-span-5 text-sm font-medium text-gray-900 flex items-center gap-2">
                              {expandedBrand === b._id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                              {b.name}
                            </div>
                            <div className="col-span-3 text-center">
                              <span className="inline-flex px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-sm font-bold">{b.productCount}</span>
                            </div>
                            <div className="col-span-4 text-xs text-gray-500">{new Date(b.createdAt).toLocaleDateString()}</div>
                          </div>
                          {expandedBrand === b._id && (
                            <div className="bg-gray-50 border-t px-6 py-3">
                              {!brandProducts[b._id] ? (
                                <p className="text-sm text-gray-400 py-2">Loading...</p>
                              ) : brandProducts[b._id].length === 0 ? (
                                <p className="text-sm text-gray-400 py-2">No products found for this brand</p>
                              ) : (
                                <div className="space-y-1.5">
                                  <p className="text-xs text-gray-500 font-medium mb-2">{brandProducts[b._id].length} products:</p>
                                  {brandProducts[b._id].map(p => (
                                    <div key={p._id} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm border">
                                      <div className="flex-1 min-w-0">
                                        <span className="font-medium text-gray-900 truncate block">{p.name}</span>
                                        <span className="text-xs text-gray-400">{p.category?.replace(/_/g, ' ')} {p.wholesaler?.companyName ? `• ${p.wholesaler.companyName}` : ''}</span>
                                      </div>
                                      <span className="text-sm font-semibold text-gray-700 ml-3">
                                        €{(p.pricing?.unitPrice || p.pricing?.basePrice || 0).toFixed(2)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </main>
      </div>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminCatalogDiscounts;